export const PAYROLL_ROSTER_FIELD_TYPE = {
  text: "text",
  number: "number",
  boolean: "boolean",
  fixedOption: "fixed_option",
} as const;

export type PayrollRosterFieldType =
  (typeof PAYROLL_ROSTER_FIELD_TYPE)[keyof typeof PAYROLL_ROSTER_FIELD_TYPE];

// UI-only display formatting for `number` fields. The stored value is always a
// plain number; this only controls how it renders / which adornment shows.
export const PAYROLL_ROSTER_FIELD_FORMAT = {
  plain: "plain",
  currency: "currency",
  percent: "percent",
} as const;

export type PayrollRosterFieldFormat =
  (typeof PAYROLL_ROSTER_FIELD_FORMAT)[keyof typeof PAYROLL_ROSTER_FIELD_FORMAT];

// API rosters are usually normalized to numeric strings, but imported/legacy
// published snapshots may still contain JSON numbers. Keep the raw type honest;
// display and comparison helpers normalize number fields where needed.
export type PayrollRosterValue = string | number | boolean | null;

// Form inputs use numeric strings; API values retain their original types.
export type PayrollRosterFormValue = Exclude<PayrollRosterValue, number>;
export type PayrollRosterFormValues = Omit<
  PayrollRosterMember,
  "field_values"
> & {
  field_values: Record<string, PayrollRosterFormValue>;
};

export interface PayrollRosterField {
  key: string;
  label: string;
  field_type: PayrollRosterFieldType;
  required: boolean;
  options: string[];
  position: number;
  default?: PayrollRosterValue;
  // Display format for `number` fields (UI-only).
  format?: PayrollRosterFieldFormat;
}

export interface PayrollTeam {
  id: string;
  name: string;
}

export interface PayrollRosterMember {
  user_id?: string | null;
  team_access_key_ids?: string[];
  team_access_keys?: PayrollTeam[];
  service_titan_employee_id: string;
  display_name: string;
  field_values: Record<string, PayrollRosterValue>;
}

export interface PayrollRosterUserSuggestion extends PayrollRosterMember {
  user_id: string;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  phone_number?: string | null;
}

export interface PayrollRosterDraftMember {
  id: string;
  isNew: boolean;
  member: PayrollRosterMember;
  suggestion?: PayrollRosterUserSuggestion;
}

export type PayrollRosterRowFocus = "missing" | "departed" | null;

export const ROSTER_FILTER_KIND = {
  options: "options",
  boolean: "boolean",
  range: "range",
  text: "text",
} as const;

export type RosterFilterKind =
  (typeof ROSTER_FILTER_KIND)[keyof typeof ROSTER_FILTER_KIND];

interface RosterFilterBase {
  includeBlank: boolean;
}

export interface RosterOptionsFilter extends RosterFilterBase {
  kind: typeof ROSTER_FILTER_KIND.options;
  values: string[];
}

export interface RosterBooleanFilter extends RosterFilterBase {
  kind: typeof ROSTER_FILTER_KIND.boolean;
  values: boolean[];
}

// Keep number bounds as raw text so partial input such as "-" or "1."
// remains controlled without accidentally narrowing the roster.
export interface RosterRangeFilter extends RosterFilterBase {
  kind: typeof ROSTER_FILTER_KIND.range;
  min: string;
  max: string;
}

export interface RosterTextFilter extends RosterFilterBase {
  kind: typeof ROSTER_FILTER_KIND.text;
  query: string;
}

export type RosterFieldFilter =
  | RosterOptionsFilter
  | RosterBooleanFilter
  | RosterRangeFilter
  | RosterTextFilter;

export type PayrollRosterFieldFilters = Record<string, RosterFieldFilter>;

export interface RosterFieldValueSummary {
  counts: Record<string, number>;
  blank: number;
  min: number | null;
  max: number | null;
}

export interface PayrollRosterFilterField {
  field: PayrollRosterField;
  options: string[];
  summary: RosterFieldValueSummary;
}

export type PayrollRosterEditTarget =
  | { mode: "edit"; draftId: string }
  | { mode: "new" }
  | { mode: "suggestion"; userId: string }
  | null;

// The published script's contract governs the roster SHAPE (which columns
// exist, their types, and the allowed fixed_option choices). Per-tech VALUES
// stay freely editable. Keyed by field key; empty ({}) means ungoverned.
export interface PayrollRosterContractField {
  type: PayrollRosterFieldType;
  required: boolean;
  informational: boolean;
  supported_options: string[];
  format?: PayrollRosterFieldFormat;
}

export type PayrollRosterContract = Record<string, PayrollRosterContractField>;

// The PUBLISHED roster the working copy is diffed against. Returned alongside
// the working copy so "unpublished changes" survives a refresh.
export interface PayrollRosterBaseline {
  version_number?: number;
  published_at?: string | null;
  fields: PayrollRosterField[];
  members: PayrollRosterMember[];
  removed_member_external_ids?: string[];
}

// The server returns the WORKING copy (the persisted draft when one exists,
// otherwise the published roster) at the top level, plus `is_draft` and the
// published `baseline` for diffing.
export interface PayrollRoster {
  team_scope_active?: boolean;
  available_teams?: PayrollTeam[];
  version_number?: number;
  status?: string;
  published_at?: string | null;
  is_draft?: boolean;
  fields: PayrollRosterField[];
  members: PayrollRosterMember[];
  contract?: PayrollRosterContract;
  baseline?: PayrollRosterBaseline | null;
  missing_user_suggestions?: PayrollRosterUserSuggestion[];
  // ServiceTitan employee IDs of members Volca can no longer match to an active
  // user — recommended for removal, never a publish blocker.
  departed_member_ids?: string[];
  // ServiceTitan employee IDs this roster says are not on payroll: removed
  // members and dismissed suggestions alike. Not offered as missing.
  removed_member_external_ids?: string[];
}

interface PayrollRosterTechnicianSyncBase {
  id: string;
  poll_after_ms: number | null;
  max_poll_interval_ms: number | null;
  poll_timeout_ms: number | null;
}

export interface CompletedPayrollRosterTechnicianSync
  extends PayrollRosterTechnicianSyncBase {
  status: "completed";
  missing_user_suggestions: PayrollRosterUserSuggestion[];
  departed_member_ids: string[];
}

export type PayrollRosterTechnicianSync =
  | (PayrollRosterTechnicianSyncBase & {
      status: "queued" | "running";
    })
  | CompletedPayrollRosterTechnicianSync
  | (PayrollRosterTechnicianSyncBase & {
      status: "failed";
      error?: string;
    });

export interface SyncPayrollRosterTechniciansResponse {
  technician_sync: PayrollRosterTechnicianSync;
}

// Body for PUT /draft — the full current working fields + members.
export interface SavePayrollRosterDraftPayload {
  roster: Pick<PayrollRoster, "fields" | "members"> & {
    removed_member_external_ids?: string[];
  };
}
